import React, { useState, useRef, useEffect } from 'react';
import { ChiffragePhase } from '../types/Timeline';

interface PhaseBarProps {
  phases: ChiffragePhase[];
  activePhaseId: string;
  activeVersionName: string;
  onSelectPhase: (id: string) => void;
  onAdvanceToPhase: (id: string) => void;
  onRenamePhase: (id: string, name: string) => void;
}

interface PhaseChipProps {
  phase: ChiffragePhase;
  activePhase: ChiffragePhase;
  activePhaseId: string;
  activeVersionName: string;
  onSelectPhase: (id: string) => void;
  onAdvanceToPhase: (id: string) => void;
  onRenamePhase: (id: string, name: string) => void;
}

const PhaseChip: React.FC<PhaseChipProps> = ({
  phase,
  activePhase,
  activePhaseId,
  activeVersionName,
  onSelectPhase,
  onAdvanceToPhase,
  onRenamePhase,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(phase.name);
  const inputRef = useRef<HTMLInputElement>(null);

  const isActive = phase.id === activePhaseId;
  const isAccessible = phase.versions.length > 0 && !isActive;
  const isFuture = phase.versions.length === 0 && phase.order > activePhase.order;

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleDoubleClick = () => {
    if (isActive || isAccessible) {
      setEditValue(phase.name);
      setIsEditing(true);
    }
  };

  const commitEdit = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== phase.name) onRenamePhase(phase.id, trimmed);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') commitEdit();
    if (e.key === 'Escape') setIsEditing(false);
  };

  if (isActive) {
    return (
      <div
        className="flex items-center gap-1 px-2 py-0.5 rounded bg-violet-600 text-white cursor-default select-none"
        onDoubleClick={handleDoubleClick}
        title="Double-clic pour renommer"
      >
        {isEditing ? (
          <input
            ref={inputRef}
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={handleKeyDown}
            className="bg-transparent text-white text-xs font-medium outline-none w-24 border-b border-violet-300"
            onClick={e => e.stopPropagation()}
          />
        ) : (
          <span className="text-xs font-medium">{phase.name}</span>
        )}
        {!isEditing && (
          <span className="px-1 py-px bg-white/20 text-white text-[10px] font-semibold rounded leading-none">
            {activeVersionName}
          </span>
        )}
      </div>
    );
  }

  if (isAccessible) {
    return (
      <button
        type="button"
        onClick={() => onSelectPhase(phase.id)}
        onDoubleClick={handleDoubleClick}
        className="px-2 py-0.5 rounded text-xs font-medium text-violet-600 hover:bg-violet-50 transition-colors select-none"
        title="Double-clic pour renommer"
      >
        {isEditing ? (
          <input
            ref={inputRef}
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={handleKeyDown}
            className="bg-transparent text-violet-600 text-xs outline-none w-24 border-b border-violet-300"
            onClick={e => e.stopPropagation()}
          />
        ) : phase.name}
      </button>
    );
  }

  if (isFuture) {
    return (
      <button
        type="button"
        onClick={() => onAdvanceToPhase(phase.id)}
        className="px-2 py-0.5 rounded text-xs text-gray-400 hover:text-violet-500 hover:bg-violet-50 transition-colors select-none"
        title={`Démarrer "${phase.name}"`}
      >
        {phase.name}
      </button>
    );
  }

  return (
    <span className="px-2 py-0.5 text-xs text-gray-300 select-none cursor-not-allowed">
      {phase.name}
    </span>
  );
};

const PhaseBar: React.FC<PhaseBarProps> = ({
  phases,
  activePhaseId,
  activeVersionName,
  onSelectPhase,
  onAdvanceToPhase,
  onRenamePhase,
}) => {
  const activePhase = phases.find(p => p.id === activePhaseId)!;

  return (
    <div className="flex items-center gap-0.5 min-w-0">
      {phases.map((phase, index) => (
        <React.Fragment key={phase.id}>
          <PhaseChip
            phase={phase}
            activePhase={activePhase}
            activePhaseId={activePhaseId}
            activeVersionName={activeVersionName}
            onSelectPhase={onSelectPhase}
            onAdvanceToPhase={onAdvanceToPhase}
            onRenamePhase={onRenamePhase}
          />
          {index < phases.length - 1 && (
            <span className="text-gray-200 text-xs select-none px-0.5">›</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default PhaseBar;
